import React, { useEffect, useState } from "react";
import { FaCalendarPlus, FaEye } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import axios from "../../api/axios";
import BottomNav from "../../components/BottomNav";
import HeaderWithBack from "../../components/HeaderWithBack";
import Loading from "../../components/Loading";
import { useAuthStore } from "../../stores/authStores";

const ReferenceList: React.FC = () => {
  const navigate = useNavigate();
  const [references, setReferences] = useState<any>([]);
  const authToken = useAuthStore((state) => state.token);
  const userData = useAuthStore((state) => state.user);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!authToken || !userData?.id) return;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const res = await axios.get("reference", {
          params: { user_id: userData.id },
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });
        setReferences(res.data.data || []);
      } catch (error) {
        console.error("Error fetching references:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [authToken, userData?.id]);

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "final":
        return "bg-blue-500";
      case "pending":
        return "bg-yellow-500";
      case "expired":
      case "rejected":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  // Hanya tampilkan tombol Booking jika status aktif/approved
  const canBook = (is_booked: boolean) => {
    return !is_booked;
  };

  const handleBooking = (item: any) => {
    const hasSpeciality = item.speciality_code || item.speciality_name;
    navigate(`/reference/${item.id}/booking/doctors`, {
      state: {
        providerCode: item.provider_code,
        specialityCode: item.speciality_code,
        specialityName: item.speciality_name,
        polyCode: item.poly_code,
        polyName: item.poly_name || item.poly,
        sickLeaveDate: item.sick_leave_date,
      },
    });
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <HeaderWithBack title="Rujukan" />

      {/* Profile Box */}
      <div className="bg-white p-4">
        <div className="bg-gray-100 rounded-md p-3 flex items-center justify-between shadow-sm">
          <div className="flex items-center">
            <div className="w-8 h-8 rounded-full bg-gray-300 mr-3" />
            <span className="font-medium">
              {userData ? userData?.person?.name : ""}
              <span className="text-sm text-gray-500"> (Me)</span>
            </span>
          </div>
        </div>
      </div>

      {/* List of References */}
      {isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <Loading />
        </div>
      ) : (
        <div className="px-4 pb-20">
          {references.length === 0 ? (
            <div className="text-center text-gray-500 mt-10">
              Tidak ada rujukan
            </div>
          ) : (
            references.map((item: any, index: number) => (
              <div key={index} className="mb-4">
                <div className="bg-teal-500 text-white px-3 py-1 rounded-t-md text-sm font-medium flex items-center justify-between">
                  <span>{item.reference_date}</span>
                  <span
                    className={`text-xs px-2 py-1 rounded ${getStatusColor(item.status)}`}
                  >
                    {item.status}
                  </span>
                </div>
                <div className="border rounded-b-md p-4 shadow-sm">
                  <p className="font-semibold">
                    No. Rujukan: {item.reference_number}
                  </p>
                  <p className="font-medium mt-1">{item.provider_name}</p>
                  <p className="text-sm text-gray-600">
                    {item.speciality_name || item.poly_name || item.poly}
                  </p>

                  <div className="text-sm mt-2 mb-3 border-l-2 border-gray-300 pl-3">
                    <p>Tipe: {item.type}</p>
                    <p>Spesialisasi: {item.speciality_name || item.poly_name || '-'}</p>
                    <p>Batas Berlaku: {item.reference_expired_date}</p>
                  </div>

                  {item.doctor_note && (
                    <div className="text-sm bg-gray-50 p-2 rounded mb-3">
                      <p className="font-medium text-gray-700">
                        Catatan Dokter:
                      </p>
                      <p className="text-gray-600">{item.doctor_note}</p>
                    </div>
                  )}

                  {item.teleconsult?.prime_logs?.length > 0 && (
                    <div className="text-sm rounded px-3 mb-3">
                      <p className="font-medium text-blue-700 mb-1">Log Code</p>
                      <div className="flex flex-wrap gap-1">
                        {item.teleconsult.prime_logs.map((log: any, i: number) => (
                          <span
                            key={i}
                            className="bg-blue-100 text-blue-800 text-xs font-mono px-2 py-0.5 rounded"
                          >
                            {log.log_code}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    {canBook(item.is_booked) ? (
                      <button
                        className="bg-white border border-teal-500 text-teal-600 flex items-center px-4 py-1.5 rounded-md text-sm hover:bg-teal-50 transition-colors font-medium"
                        onClick={() => handleBooking(item)}
                      >
                        <FaCalendarPlus className="mr-2" /> Booking
                      </button>
                    ) : (
                      <button
                        className="bg-teal-500 text-white flex items-center px-4 py-1.5 rounded-md text-sm hover:bg-teal-600 transition-colors"
                        onClick={() => navigate(`/reference/${item.id}`)}
                      >
                        <FaEye className="mr-2" /> Lihat Detail
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
};

export default ReferenceList;
